import React, { useEffect, useRef } from "react";
import { decodeL10n } from "../../util/l10n";
import { PageContext } from "../../util/context";
import { RecommendationArticlesProps } from "../../types/articles";
import { usePager } from "../../util/use-pager";
import ArticleTile from "../article-tile/article-tile";
import Pager from "../pager/pager";
import PlaceholderTile from "../article-tile/placeholder-tile";
import "./articles.scss";

const RecommendationArticles = (props: RecommendationArticlesProps) => {
  const pageType =
    document.querySelector<HTMLMetaElement>("meta[name='pagetype']")?.content ?? "NoPageTypeFound";
  const pageLocation = props.location !== null ? props.location : pageType;
  const dataFragmentName = `product-recommendation-${pageLocation}`;

  const viewport = document.documentElement.clientWidth;
  const pagerApi = usePager(viewport, props.recommendations.length);
  const decodedL10n = decodeL10n(props.l10n);

  const articleTilesContainerRef = useRef<HTMLDivElement>(null);
  const touchStartPositionX = useRef(0);
  const touchStartPositionY = useRef(0);

  useEffect(() => {
    if (!articleTilesContainerRef.current) return;

    const containerWidth = articleTilesContainerRef.current.clientWidth;
    const computedStyle = getComputedStyle(articleTilesContainerRef.current);
    const gapWidth = parseFloat(computedStyle.gap);
    const pageWidth = containerWidth + gapWidth;

    articleTilesContainerRef.current.scrollTo({
      top: 0,
      left: pageWidth * (pagerApi.currentPage - 1),
      behavior: "smooth"
    });
  }, [pagerApi.currentPage]);

  const handleTouchStart = (event: React.TouchEvent) => {
    if (!articleTilesContainerRef.current) return;

    if (event.touches.length !== 1) return;
    touchStartPositionX.current = event.touches[0].clientX;
    touchStartPositionY.current = event.touches[0].clientY;
  };

  const handleTouchEnd = (event: React.TouchEvent) => {
    if (!event.changedTouches) return;

    const clientX = event.changedTouches.length
      ? event.changedTouches[0].clientX
      : touchStartPositionX.current;
    const clientY = event.changedTouches.length
      ? event.changedTouches[0].clientY
      : touchStartPositionY.current;

    const diffX = Math.abs(clientX - touchStartPositionX.current);
    const diffY = Math.abs(clientY - touchStartPositionY.current);

    // panning on y-axis is handled natively by the browser
    if (diffX > diffY) {
      clientX < touchStartPositionX.current ? pagerApi.goToNextPage() : pagerApi.goToPreviousPage();
    }
  };

  return (
    <div
      className="product-recommendation-articles-container"
      data-product-recommendation-fragment={`product-recommendation-${pageLocation}`}
    >
      <PageContext.Provider value={pagerApi}>
        <div className="header">
          <div className="spacer"></div>
          <span className="headline">{decodedL10n.takeAwayHeadline}</span>
          <Pager l10n={decodedL10n} dataFragmentName={dataFragmentName} />
        </div>

        <div
          className="article-tiles"
          ref={articleTilesContainerRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {props.recommendations.map((tile, index) => (
            <ArticleTile
              key={index}
              article={tile}
              l10n={decodedL10n}
              currencyInformation={props.currencyInformation}
              pageType={pageType}
              pageLocation={pageLocation}
              dataFragmentName={dataFragmentName}
            />
          ))}

          {pagerApi.placeholderCount > 0 &&
            [...Array(pagerApi.placeholderCount)].map((_, i) => (
              <PlaceholderTile key={i} slot={i + 1} />
            ))}
        </div>
      </PageContext.Provider>
    </div>
  );
};

export default RecommendationArticles;
